import { useState, useEffect, type RefObject } from 'react'

function extractGradientColor(value: string): string | null {
  const match = value.match(/(?:rgb|rgba)\([^)]+\)/)
  if (match) return match[0]
  const hexMatch = value.match(/#[0-9a-fA-F]{3,8}/)
  if (hexMatch) return hexMatch[0]
  return null
}

function parseColor(bg: string): [number, number, number] | null {
  // Try rgb/rgba
  const rgbMatch = bg.match(/\d+/g)
  if (rgbMatch && rgbMatch.length >= 3) {
    return [Number(rgbMatch[0]), Number(rgbMatch[1]), Number(rgbMatch[2])]
  }
  // Try hex
  const hexMatch = bg.match(/#([0-9a-fA-F]{3,8})/)
  if (hexMatch) {
    let hex = hexMatch[1]
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ]
  }
  return null
}

/**
 * Detects whether the background behind a given element is dark or light.
 * Hides the entire fixed container, samples all elements at the center point,
 * and supports rgb, hex, and gradient backgrounds.
 */
export function useBackgroundContrast(
  elementRef: RefObject<HTMLElement | null>
): 'dark' | 'light' {
  const [background, setBackground] = useState<'dark' | 'light'>('light')

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const check = () => {
      const rect = element.getBoundingClientRect()
      const x = rect.left + rect.width / 2
      const y = rect.top + rect.height / 2

      // Hide the entire fixed container so it doesn't interfere
      const fixedContainer = element.closest('.fixed') as HTMLElement | null
      const target = fixedContainer || element
      const prevVisibility = target.style.visibility
      target.style.visibility = 'hidden'

      const elements = document.elementsFromPoint(x, y)
      target.style.visibility = prevVisibility

      for (const el of elements) {
        if (el === target || target.contains(el)) continue

        const style = getComputedStyle(el)
        let bg = style.backgroundColor

        // Check for gradients
        if (bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') {
          const bgImage = style.backgroundImage
          if (bgImage && bgImage !== 'none') {
            const gradientColor = extractGradientColor(bgImage)
            if (gradientColor) bg = gradientColor
            else continue
          } else {
            continue
          }
        }

        const rgb = parseColor(bg)
        if (rgb) {
          const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255
          setBackground(luminance < 0.45 ? 'dark' : 'light')
          return
        }
      }
    }

    check()

    const observer = new IntersectionObserver(check, {
      threshold: [0, 0.5, 1],
    })
    observer.observe(element)

    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
    }
  }, [elementRef])

  return background
}
